<template>
  <div class="content-imagechoice">
    <!-- Title images section -->
    <div v-if="data.question.showImageOnTitle" class="title-images">
      <div v-for="(image, index) in data.question.imageOnTitle" :key="index" class="title-image-container"
        :class="[data.question.titleImageSize]" v-show="!image.isHidden">
        <zoomable-image
          :image="image"
          :show-label="!data.question.hideTitleLabels"
          :container-class="data.question.titleImageSize"
        />
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showModal" class="image-modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-image-container">
          <img :src="selectedImage.label" :alt="selectedImage.value" class="modal-image">
          <div class="modal-close" @click="closeModal">
            <i class="material-icons">close</i>
          </div>
        </div>
      </div>
    </div>

    <!-- Subjects section -->
    <div class="subjects-container">
      <div v-for="(subject, subjectIndex) in data.question.subjects" :key="subjectIndex" :data-subject-id="subject._id"
        class="subject-section">
        <!-- Subject label above the grid -->
        <div class="subject-label">
          <h3 :style="{ color: data.response_font_color || data.texts }">{{ subject.label }}</h3>
          <div class="selection-info">
            <span>{{ getSelectionCount(subject._id) }} / {{ subject.max }} {{ $t('selected') }}</span>
          </div>
        </div>
        <div class="options-grid" :class="[data.question.optionImageSize]">
          <div v-for="(option, optionIndex) in subject.options" :key="optionIndex" v-show="!option.isHidden"
            class="option-container" :class="{
              selected: isSelected(subject._id, optionIndex),
              'limit-reached': isSelectionLimitReached(subject) && !isSelected(subject._id, optionIndex)
            }" @click="handleOptionClick(subject, option, optionIndex)">
            <zoomable-image
              :image="option"
              :show-label="!data.question.hideAllSubjectLabels"
              :container-class="data.question.optionImageSize"
              :image-class="{ selected: isSelected(subject._id, optionIndex) }"
            >
              <!-- Checkmark overlay for selected images -->
              <template #overlay>
                <div v-if="isSelected(subject._id, optionIndex)" class="checkmark-overlay">
                  <i class="material-icons">check_circle</i>
                </div>
                <!-- Limit reached overlay -->
                <div v-if="isSelectionLimitReached(subject) && !isSelected(subject._id, optionIndex)"
                  class="limit-overlay">
                  <i class="material-icons">lock</i>
                </div>
              </template>
            </zoomable-image>
          </div>
        </div>
      </div>
    </div>

    <div class="submit-options-matrix" style="text-align: center;" v-if="buttonEnabled">
      <app-button
        :widget="true"
        :binds-ripple="false"
        class="binds-raised binds-primary"
        style="box-shadow: none;background: none;"
        :style="{'color': data.buttons}"
        v-on:click="handleSubmit">{{data.question.nextLabel}}</app-button>
    </div>
  </div>
</template>

<script>
import { widgetService } from '@/_services'
import ZoomableImage from '../common/ZoomableImage.vue'

export default {
  name: 'imageChoice',
  components: {
    ZoomableImage
  },
  props: ['data'],
  data () {
    return {
      selectedOptions: [],
      showModal: false,
      selectedImage: null,
      buttonEnabled: true
    }
  },
  computed: {
    isSkippable () {
      return !this.data.question.required
    }
  },
  mounted () {
    if (this.data.question.required === true) {
      this.buttonEnabled = false
    }
    this.updateButtonVisibility()
  },
  beforeDestroy () {
    // Clean up event listener
    this.$root.$off('footer-submit', this.handleSubmit)
  },
  methods: {
    isSelected (subjectId, optionIndex) {
      return this.selectedOptions.some(sel =>
        sel.subjectId === subjectId && sel.optionIndex === optionIndex
      )
    },

    isSelectionLimitReached (subject) {
      const selections = this.selectedOptions.filter(sel => sel.subjectId === subject._id)
      return selections.length >= subject.max
    },

    isSubjectValid (subject) {
      const selections = this.selectedOptions.filter(sel => sel.subjectId === subject._id)
      return selections.length >= subject.min
    },

    updateButtonVisibility () {
      if (this.data.question.required) {
        // Show button if all required subjects meet their minimum selections
        const allSubjectsValid = this.data.question.subjects.every(subject => this.isSubjectValid(subject))
        this.buttonEnabled = allSubjectsValid
      } else {
        // For non-required questions, show button if there's at least one selection
        const hasSelections = this.selectedOptions.length > 0
        this.buttonEnabled = hasSelections || this.isSkippable
      }
    },

    selectOption (subjectId, option, optionIndex) {
      const subject = this.data.question.subjects.find(s => s._id === subjectId)
      if (!subject) return

      const existingSelections = this.selectedOptions.filter(sel => sel.subjectId === subjectId)
      const isCurrentlySelected = this.isSelected(subjectId, optionIndex)

      if (isCurrentlySelected) {
        // Remove if already selected
        this.selectedOptions = this.selectedOptions.filter(sel =>
          !(sel.subjectId === subjectId && sel.optionIndex === optionIndex)
        )
      } else if (existingSelections.length < subject.max) {
        // Add new selection if within limits
        this.selectedOptions.push({
          subjectId: subjectId,
          optionIndex: optionIndex,
          optionId: option._id,
          value: option.value
        })

        // Check if we just reached the max for this subject
        if (existingSelections.length + 1 === subject.max) {
          // Find the next subject that needs answers
          const currentSubjectIndex = this.data.question.subjects.findIndex(s => s._id === subjectId)
          const nextIncompleteSubject = this.data.question.subjects.slice(currentSubjectIndex + 1)
            .find(s => {
              const selections = this.selectedOptions.filter(sel => sel.subjectId === s._id)
              return selections.length < s.max
            })

          // Use setTimeout to ensure the DOM has updated
          setTimeout(() => {
            if (nextIncompleteSubject) {
              // Scroll to next subject that needs answers
              const nextSubject = document.querySelector(`[data-subject-id="${nextIncompleteSubject._id}"]`)
              if (nextSubject) {
                const container = document.querySelector('.content-imagechoice')
                const containerRect = container.getBoundingClientRect()
                const elementRect = nextSubject.getBoundingClientRect()
                const scrollTop = elementRect.top - containerRect.top + container.scrollTop - 20

                container.scrollTo({
                  top: scrollTop,
                  behavior: 'smooth'
                })
              }
            } else if (this.data.question.subjects.every(subject => this.isSubjectValid(subject))) {
              // If all subjects are complete, scroll to footer
              const submitButton = document.querySelector('.submit-options-matrix')
              if (submitButton) {
                const container = document.querySelector('.content-imagechoice')
                const containerRect = container.getBoundingClientRect()
                const elementRect = submitButton.getBoundingClientRect()
                const scrollTop = elementRect.top - containerRect.top + container.scrollTop - 20

                container.scrollTo({
                  top: scrollTop,
                  behavior: 'smooth'
                })
              }
            }
          }, 100)
        }
      }

      this.updateButtonVisibility()
    },

    handleSubmit () {
      if (this.isSkippable || this.data.question.subjects.every(subject => this.isSubjectValid(subject))) {
        const formattedSelections = this.selectedOptions.map(sel => ({
          _id: sel.subjectId,
          optionId: sel.optionId,
          value: sel.value
        }))

        if (this.data.is_to_post) {
          this.$root.$emit('setLoading', true)
          let dataToSend = formattedSelections.length > 0
            ? { value: formattedSelections, sending: this.data.sending }
            : { skip: true, sending: this.data.sending }

          widgetService.checkIfNextOrFirstQuestion(this.data, dataToSend)
            .then(questionUpdated => {
              this.$root.$emit('questionUpdated', questionUpdated)
            })
        }
      }
    },

    getSelectionCount (subjectId) {
      return this.selectedOptions.filter(sel => sel.subjectId === subjectId).length
    },

    handleOptionClick (subject, option, optionIndex) {
      if (this.isSelectionLimitReached(subject) && !this.isSelected(subject._id, optionIndex)) {
        return
      }
      this.selectOption(subject._id, option, optionIndex)
    },

    openImageModal (image) {
      this.selectedImage = image
      this.showModal = true
      document.body.style.overflow = 'hidden'
    },

    closeModal () {
      this.showModal = false
      document.body.style.overflow = 'auto'
    }
  },
  watch: {
    selectedOptions: {
      handler () {
        this.updateButtonVisibility()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
.content-imagechoice {
  width: 100%;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  .title-images {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 150px));
    gap: 20px;
    margin-bottom: 30px;
    width: 100%;
    padding: 10px 15px;
    justify-content: center;

    .title-image-container {
      width: 100%;
      margin: 0 auto;

      &.large {
        max-width: 400px;
        grid-column: span 2;
      }
    }
  }

  .subjects-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0 15px;

    .subject-section {
      width: 100%;
      padding: 15px 0;
      position: relative;

      .subject-label {
        width: 100%;
        margin-bottom: 20px;
        text-align: center;
        position: relative;

        h3 {
          font-size: 1.1em;
          margin: 0 0 10px 0;
          font-weight: 500;
          word-break: break-word;
          line-height: 1.4;
        }

        .selection-info {
          font-size: 0.85em;
          opacity: 0.8;
          margin-top: 8px;
        }
      }

      .options-grid {
        display: grid;
        gap: 15px;
        width: 100%;
        justify-content: center;

        &.small {
          grid-template-columns: repeat(auto-fit, minmax(90px, 100px));
        }

        &.medium {
          grid-template-columns: repeat(auto-fit, minmax(120px, 150px));
        }

        &.large {
          grid-template-columns: repeat(auto-fit, minmax(130px, 200px));
        }

        .option-container {
          width: 100%;
          cursor: pointer;
          transition: transform 0.2s ease;

          &.selected :deep(.image-wrapper) {
            border: 2px solid var(--actionColor);
            box-shadow: 0 0 0 2px rgba(var(--actionColor-rgb), 0.3);
          }

          &.limit-reached {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .checkmark-overlay,
          .limit-overlay {
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 3;

            i {
              color: white;
              font-size: 36px;
              filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
            }
          }
        }
      }
    }
  }

  .image-modal {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.87);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
    animation: fadeIn 0.15s ease-out;

    .modal-content {
      position: relative;
      width: auto;
      height: auto;
      max-width: 70%;
      max-height: 70%;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: zoomIn 0.15s ease-out;
      will-change: transform, opacity;

      .modal-image-container {
        position: relative;
        width: auto;
        height: auto;
        display: flex;
        align-items: center;
        justify-content: center;

        .modal-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 4px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .modal-close {
          position: absolute;
          top: -40px;
          right: -40px;
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.98);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease-out;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
          z-index: 10000;

          i {
            font-size: 20px;
            color: #333;
          }

          &:hover {
            background: #fff;
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          }
        }
      }
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media only screen and (max-width: 600px) {
  .title-images {
    padding-top: 7vh !important;
  }

  .content-imagechoice {
    padding: 10px;
    max-height: 100%;

    .title-images {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      padding: 10px;
      gap: 10px;

      .title-image-container {
        &.large {
          min-width: 160px;
        }
        .option-label {
          margin-top: 8px;
          font-size: 0.9em;
        }
      }
    }

    .subjects-container {
      padding: 0 10px;

      .subject-section {
        padding: 10px 0;

        .subject-label {
          h3 {
            font-size: 1em;
            line-height: 1.3;
          }
        }

        .options-grid {
          gap: 10px;
        }
      }
    }

    .image-modal {
      padding: 15px;

      .modal-content {
        max-width: 85%;
        max-height: 85%;

        .modal-close {
          top: -15px;
          right: -15px;
          width: 28px;
          height: 28px;

          i {
            font-size: 18px;
          }
        }
      }
    }
  }
}
</style>
